import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Share2, Heart, Star } from 'lucide-react';
import { getHomeBySlug, getRelatedHomes, getCategoryDisplayName, getHomeDisplayName } from '../lib/homes';
import { HomeCategory } from '../types';
import SpecsTable from '../components/SpecsTable';
import { InteractiveFloorPlanViewer } from '../components/InteractiveFloorPlanViewer';
import Accordion from '../components/Accordion';
import EnhancedEnquiryForm from '../components/EnhancedEnquiryForm';
import Carousel from '../components/Carousel';

const HomeDetailPage: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const [selectedImage, setSelectedImage] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  const categoryKey = category as HomeCategory;
  const home = getHomeBySlug(slug!, categoryKey);
  const categoryName = getCategoryDisplayName(categoryKey);

  if (!home) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1F2937] mb-4">Home Not Found</h1>
          <p className="text-[#334155] mb-8">The home you're looking for doesn't exist.</p>
          <Link
            to="/homes"
            className="bg-[#FF5A1F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#e4501c] transition-colors duration-200"
          >
            View All Homes
          </Link>
        </div>
      </div>
    );
  }

  const relatedHomes = getRelatedHomes(home.id, home.category);

  const accordionItems = [
    {
      title: 'Inclusions',
      content: home.inclusions
    },
    {
      title: 'Optional Extras',
      content: home.options
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>{getHomeDisplayName(home)} - {categoryName} | Rolleston Homes</title>
        <meta name="description" content={`${getHomeDisplayName(home)} - ${home.keyFeatures.slice(0, 3).join(', ')}. ${home.specs.bedrooms} bedrooms, ${home.specs.bathrooms} bathrooms, ${home.specs.totalArea}m².`} />
      </Helmet>

      {/* Sticky Header */}
      <div className="bg-white shadow-sm sticky top-16 z-40">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={`/homes/${category}`}
                className="flex items-center space-x-2 text-[#FF5A1F] hover:text-[#e4501c] transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to {categoryName}</span>
              </Link>
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-[#1F2937]">{getHomeDisplayName(home)}</h1>
                {home.priceRange && (
                  <p className="text-[#FF5A1F] font-semibold">{home.priceRange}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200">
                <Share2 className="w-5 h-5" />
              </button>
              <button className="p-2 text-[#334155] hover:text-red-500 transition-colors duration-200">
                <Heart className="w-5 h-5" />
              </button>
              <a
                href="#enquiry"
                className="bg-[#FF5A1F] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e4501c] transition-colors duration-200"
              >
                Enquire Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200">
              Home
            </Link>
            <span className="text-gray-400">/</span>
            <Link to="/homes" className="text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200">
              All Homes
            </Link>
            <span className="text-gray-400">/</span>
            <Link to={`/homes/${category}`} className="text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200">
              {categoryName}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-[#FF5A1F] font-medium">{getHomeDisplayName(home)}</span>
          </nav>
        </div>
      </div>

      {/* Hero Gallery */}
      <section className="bg-white py-8">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Image */}
            <div className="lg:col-span-2">
              <div className="relative aspect-video rounded-2xl overflow-hidden">
                <img
                  src={home.gallery[selectedImage]}
                  alt={`${home.name} - Image ${selectedImage + 1}`}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setShowLightbox(true)}
                />
                {home.isFeatured && (
                  <div className="absolute top-4 left-4 bg-[#FF5A1F] text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                    <Star className="w-4 h-4 fill-current" />
                    <span>Featured</span>
                  </div>
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {home.gallery.length > 1 && (
                <div className="flex space-x-4 mt-4 overflow-x-auto">
                  {home.gallery.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors duration-200 ${
                        selectedImage === index ? 'border-[#FF5A1F]' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${home.name} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Home Info */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <span className="px-3 py-1 bg-[#FF5A1F]/10 text-[#FF5A1F] rounded-full text-sm font-medium">
                    {categoryName}
                  </span>
                </div>
                <h1 className="text-3xl font-extrabold text-[#1F2937] mb-2">{getHomeDisplayName(home)}</h1>
                {home.priceRange && (
                  <p className="text-2xl font-bold text-[#FF5A1F] mb-4">{home.priceRange}</p>
                )}
                <p className="text-[#334155] leading-relaxed">
                  {home.keyFeatures.slice(0, 3).join(' • ')}
                </p>
              </div>

              {/* Quick Specs */}
              <div className="bg-[#FAFAFA] rounded-xl p-6">
                <h3 className="font-bold text-[#1F2937] mb-4">Quick Specs</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#334155]">Bedrooms</span>
                    <div className="font-bold text-[#1F2937]">{home.specs.bedrooms}</div>
                  </div>
                  <div>
                    <span className="text-[#334155]">Bathrooms</span>
                    <div className="font-bold text-[#1F2937]">{home.specs.bathrooms}</div>
                  </div>
                  {home.specs.garage && (
                    <div>
                      <span className="text-[#334155]">Garage</span>
                      <div className="font-bold text-[#1F2937]">{home.specs.garage}</div>
                    </div>
                  )}
                  <div>
                    <span className="text-[#334155]">Total Area</span>
                    <div className="font-bold text-[#1F2937]">{home.specs.totalArea}m²</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Floor Plans */}
            <InteractiveFloorPlanViewer
              floorPlans={home.floorPlans}
              brochureUrl={home.brochureUrl}
              virtualTourUrl={home.virtualTourUrl}
            />

            {/* Key Features */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-[#1F2937] mb-6">Key Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {home.keyFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#FF5A1F] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-[#334155]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Inclusions & Options */}
            <Accordion title="Inclusions & Options" items={accordionItems} />
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Specifications */}
            <SpecsTable specs={home.specs} />

            {/* Enquiry Form */}
            <div id="enquiry">
              <EnhancedEnquiryForm 
                homeInterest={getHomeDisplayName(home)}
                showProgress={true}
                variant="default"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Related Homes */}
      {relatedHomes.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-[1280px] mx-auto px-6 md:px-8">
            <Carousel
              homes={relatedHomes}
              title="You May Also Like"
            />
            
            {/* Cross-category suggestion */}
            <div className="text-center mt-12">
              <p className="text-[#334155] mb-4">
                Looking for something different?
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {(['core', 'family', 'kiwiana'] as HomeCategory[])
                  .filter(cat => cat !== home.category)
                  .map(cat => (
                    <Link
                      key={cat}
                      to={`/homes/${cat}`}
                      className="px-6 py-3 border-2 border-[#FF5A1F] text-[#FF5A1F] rounded-lg font-semibold hover:bg-[#FF5A1F] hover:text-white transition-all duration-200"
                    >
                      View {getCategoryDisplayName(cat)}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setShowLightbox(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl font-bold z-10"
            >
              ×
            </button>
            <img
              src={home.gallery[selectedImage]}
              alt={`${home.name} - Full size`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeDetailPage;